export type ExportMarket = {
  id: string;
  country: string;
  iso2: string;
  wbCode: string;
  region: string;
  x: number;
  y: number;
  latitude: number;
  longitude: number;
  score: number;
  demandIndex: number;
  momentum: number;
  competition: "Rendah" | "Sedang" | "Tinggi";
  entryDifficulty: "Mudah" | "Menengah" | "Kompleks";
  leadChannel: string;
  recommendedOffer: string;
  insight: string;
  requirements: string[];
  buyerTypes: string[];
};

export type ExportProductProfile = {
  id: string;
  label: string;
  hsCode: string;
  description: string;
  markets: ExportMarket[];
};

const craftMarkets: ExportMarket[] = [
  {
    id: "sg",
    country: "Singapura",
    iso2: "SG",
    wbCode: "SGP",
    region: "Asia Tenggara",
    x: 704,
    y: 323,
    latitude: 1.3521,
    longitude: 103.8198,
    score: 91,
    demandIndex: 87,
    momentum: 12.4,
    competition: "Sedang",
    entryDifficulty: "Mudah",
    leadChannel: "Boutique retail & hospitality",
    recommendedOffer: "Low MOQ, premium packaging, fast replenishment",
    insight: "Pasar dekat dengan daya beli tinggi. Cocok untuk uji ekspor awal dan koleksi premium berukuran kecil.",
    requirements: ["Commercial invoice", "Packing list", "Country of origin"],
    buyerTypes: ["Lifestyle retailer", "Hotel supplier", "Gift curator"],
  },
  {
    id: "my",
    country: "Malaysia",
    iso2: "MY",
    wbCode: "MYS",
    region: "Asia Tenggara",
    x: 690,
    y: 312,
    latitude: 4.2105,
    longitude: 101.9758,
    score: 88,
    demandIndex: 84,
    momentum: 10.2,
    competition: "Sedang",
    entryDifficulty: "Mudah",
    leadChannel: "Distributor & marketplace cross-border",
    recommendedOffer: "Retail-ready collection with Malay/English catalog",
    insight: "Kedekatan budaya dan logistik membuat Malaysia relevan untuk produk dekorasi rumah dan kerajinan natural.",
    requirements: ["Commercial invoice", "Packing list", "Product material declaration"],
    buyerTypes: ["Homeware distributor", "Marketplace seller", "Corporate gift buyer"],
  },
  {
    id: "au",
    country: "Australia",
    iso2: "AU",
    wbCode: "AUS",
    region: "Oseania",
    x: 815,
    y: 407,
    latitude: -25.2744,
    longitude: 133.7751,
    score: 84,
    demandIndex: 82,
    momentum: 8.8,
    competition: "Tinggi",
    entryDifficulty: "Menengah",
    leadChannel: "Ethical homeware & interior store",
    recommendedOffer: "Sustainable story, material traceability, private label",
    insight: "Buyer menilai desain, konsistensi kualitas, dan cerita keberlanjutan. Potensi harga jual lebih tinggi, tetapi dokumen material perlu rapi.",
    requirements: ["Material declaration", "Fumigation treatment when required", "Supplier code of conduct"],
    buyerTypes: ["Ethical retailer", "Interior studio", "Import wholesaler"],
  },
  {
    id: "jp",
    country: "Jepang",
    iso2: "JP",
    wbCode: "JPN",
    region: "Asia Timur",
    x: 842,
    y: 220,
    latitude: 36.2048,
    longitude: 138.2529,
    score: 81,
    demandIndex: 79,
    momentum: 6.4,
    competition: "Tinggi",
    entryDifficulty: "Kompleks",
    leadChannel: "Specialty retailer & trading company",
    recommendedOffer: "Precise specification, neutral palette, strict QC",
    insight: "Pasar menarik untuk desain minimal dan kualitas tinggi. Buyer biasanya meminta konsistensi ukuran, warna, dan inspeksi yang ketat.",
    requirements: ["Detailed product specification", "Quality inspection standard", "Japanese label support"],
    buyerTypes: ["Trading company", "Department store buyer", "Design retailer"],
  },
  {
    id: "de",
    country: "Jerman",
    iso2: "DE",
    wbCode: "DEU",
    region: "Eropa",
    x: 510,
    y: 190,
    latitude: 51.1657,
    longitude: 10.4515,
    score: 78,
    demandIndex: 83,
    momentum: 5.9,
    competition: "Tinggi",
    entryDifficulty: "Kompleks",
    leadChannel: "Sustainable wholesaler & fair trade retail",
    recommendedOffer: "Compliance pack, traceability, consolidated shipment",
    insight: "Volume pasar besar, tetapi buyer menuntut dokumentasi, ketertelusuran bahan, dan klaim lingkungan yang dapat dibuktikan.",
    requirements: ["EU product safety information", "Material traceability", "Packaging compliance"],
    buyerTypes: ["Fair trade wholesaler", "Home décor importer", "Sustainable marketplace"],
  },
  {
    id: "nl",
    country: "Belanda",
    iso2: "NL",
    wbCode: "NLD",
    region: "Eropa",
    x: 490,
    y: 184,
    latitude: 52.1326,
    longitude: 5.2913,
    score: 80,
    demandIndex: 81,
    momentum: 7.1,
    competition: "Tinggi",
    entryDifficulty: "Menengah",
    leadChannel: "European distribution hub",
    recommendedOffer: "Container consolidation and seasonal collection",
    insight: "Belanda berperan sebagai hub distribusi Eropa. Cocok untuk buyer yang menggabungkan beberapa kategori produk Indonesia.",
    requirements: ["Commercial documents", "EU packaging information", "Consistent master carton"],
    buyerTypes: ["Importer-distributor", "Concept store group", "Sourcing agent"],
  },
  {
    id: "ae",
    country: "Uni Emirat Arab",
    iso2: "AE",
    wbCode: "ARE",
    region: "Timur Tengah",
    x: 605,
    y: 282,
    latitude: 23.4241,
    longitude: 53.8478,
    score: 76,
    demandIndex: 75,
    momentum: 11.1,
    competition: "Sedang",
    entryDifficulty: "Menengah",
    leadChannel: "Hospitality procurement & premium gifting",
    recommendedOffer: "Luxury packaging and project-based custom order",
    insight: "Peluang kuat pada proyek hospitality, hampers premium, dan dekorasi. Respons cepat terhadap permintaan custom menjadi nilai tambah.",
    requirements: ["English/Arabic label support", "Commercial invoice", "Project quotation"],
    buyerTypes: ["Hospitality supplier", "Corporate gift company", "Premium retailer"],
  },
  {
    id: "us",
    country: "Amerika Serikat",
    iso2: "US",
    wbCode: "USA",
    region: "Amerika Utara",
    x: 185,
    y: 225,
    latitude: 37.0902,
    longitude: -95.7129,
    score: 74,
    demandIndex: 90,
    momentum: 4.8,
    competition: "Tinggi",
    entryDifficulty: "Kompleks",
    leadChannel: "Specialty e-commerce & wholesale marketplace",
    recommendedOffer: "Distinctive story, strong photography, scalable MOQ",
    insight: "Permintaan besar tetapi persaingan sangat tinggi. Produk perlu memiliki desain khas, foto profesional, dan struktur harga grosir yang jelas.",
    requirements: ["Country-of-origin marking", "Material declaration", "Product liability readiness"],
    buyerTypes: ["Online wholesaler", "Independent retailer", "Interior brand"],
  },
];

const foodMarkets = craftMarkets.map((market) => ({
  ...market,
  score: Math.max(68, Math.min(94, market.score + (market.id === "my" ? 5 : market.id === "sg" ? 3 : market.id === "jp" ? 2 : -2))),
  recommendedOffer: market.id === "my" || market.id === "sg" ? "Halal-ready retail pack with consistent shelf life" : "Clear ingredients, shelf life, and distributor margin",
  requirements: ["Ingredient list", "Shelf-life evidence", "Food safety documentation"],
}));

const coffeeMarkets = craftMarkets.map((market) => ({
  ...market,
  score: Math.max(70, Math.min(95, market.score + (market.id === "jp" ? 7 : market.id === "de" ? 6 : market.id === "us" ? 5 : 0))),
  recommendedOffer: "Origin story, cupping profile, sample kit, and wholesale pricing",
  requirements: ["Product specification", "Certificate of origin", "Roast/green bean quality profile"],
}));

export const exportProductProfiles: ExportProductProfile[] = [
  {
    id: "craft",
    label: "Kerajinan anyaman",
    hsCode: "HS 4602",
    description: "Basketwork, wickerwork, dan produk anyaman dari bahan nabati.",
    markets: craftMarkets,
  },
  {
    id: "food",
    label: "Makanan olahan",
    hsCode: "HS 2103",
    description: "Saus, sambal, bumbu campuran, dan produk olahan sejenis.",
    markets: foodMarkets,
  },
  {
    id: "coffee",
    label: "Kopi",
    hsCode: "HS 0901",
    description: "Kopi roasted maupun green bean untuk pasar retail dan B2B.",
    markets: coffeeMarkets,
  },
];

export const exportDataSources = [
  {
    label: "UN Comtrade",
    detail: "Referensi arus perdagangan berdasarkan HS code.",
    url: "https://comtradeplus.un.org/",
  },
  {
    label: "World Bank Indicators API",
    detail: "Data makro negara terbaru tanpa API key.",
    url: "https://api.worldbank.org/v2/",
  },
];
